/**
 * Pure feasibility contract for strategy generation.
 *
 * Shared by the React intake dialog, the builder-service intake controller,
 * and the CLI to decide whether generation can proceed, and to explain why not.
 * No imports — keep this file dependency-free so all three consumers can use it as-is.
 */

export type FeasibilityState = 'checking' | 'ready' | 'blocked';

export type FeasibilityBlockerCode =
  | 'no_task_routes'
  | 'analysis_not_pinned'
  | 'required_variable_no_home'
  | 'empty_variable_package'
  /** W13 (IS1, AC-W13-04): a selected task is left with zero selected theses. */
  | 'task_without_theses'
  /** W13 (IS1, AC-W13-21): a selected thesis id the pinned analysis does not contain. */
  | 'insight_not_in_analysis';

export type FeasibilityWarningCode =
  | 'single_route_no_router'
  /** W13 (IS1, AC-W13-02/20/25): the selection is wider than the scope's prompt budget. */
  | 'insight_selection_over_budget'
  /** W13 (IS1, fix round 1 / I4): the selection emptied an insight variable's answer options. */
  | 'insight_variable_without_options';

export interface FeasibilityWarning {
  code: FeasibilityWarningCode;
  message: string;
  /**
   * What the warning is ABOUT — a scope key (`global` / a task id) or a variable
   * id. One verdict can carry several warnings of the same code, so the row that
   * renders them needs this to stay addressable.
   */
  scopeId?: string;
}

/**
 * W13 ruling R2: a manual selection overrides the top-N projection, and the
 * prompt is allowed to grow — up to 2× the default shortlist per scope. Above
 * that the owner is TOLD (a warning row, never a blocker): the prompt and its
 * cost grow with every extra thesis.
 *
 * 2 × `GLOBAL_INSIGHTS_TOP_N` (8) and 2 × `PER_TASK_INSIGHTS_TOP_N` (6).
 */
export const GLOBAL_INSIGHT_SELECTION_BUDGET = 16;
export const TASK_INSIGHT_SELECTION_BUDGET = 12;

export interface FeasibilityVariable {
  id: string;
  name?: string;
  required?: boolean;
  priority?: string;
  taskIds?: string[];
}

/**
 * W13 (IS1): ONE scope of the pinned analysis — the global package or one task
 * package — measured AGAINST the operator's thesis selection. The counts are
 * computed where the packages are readable (the builder-service intake gate
 * reads Firestore; the wizard reads the same packages it renders) and handed to
 * this pure contract, so both sides say the same sentence.
 */
export interface FeasibilityInsightScope {
  scope: 'global' | 'task';
  taskId?: string;
  /** The task's name — the refusal names it, never the id (AC-W13-04). */
  taskName?: string;
  /** Theses of this scope that survive the selection. */
  selectedCount: number;
  /** Theses this scope holds in total. 0 = the scope has nothing to select. */
  availableCount: number;
}

/**
 * W13 ruling W13-R22 — the scope key a selection addresses: the literal
 * `'global'` for the cross-task package, or a task id for a per-task package.
 *
 * Global and per-task theses live in DISJOINT id namespaces: each package comes
 * out of its own aggregation call with its own model-generated UUIDs
 * (`InsightsAnalysisHandler.runAggregationByCategory`, once for the analysis and
 * once per task). A producer that can only see one namespace — the edit-mode
 * picker hydrates `category_packages` only — must therefore say WHICH scopes it
 * addressed, or every other scope would look starved and the run would be
 * refused for a selection that is perfectly valid.
 */
export const GLOBAL_INSIGHT_SCOPE = 'global';

/**
 * W13 (IS1): the operator's thesis selection, resolved against the pinned
 * analysis. ABSENT (the whole object) = no selection was sent, which is today's
 * top-N projection and produces no blocker and no warning.
 */
export interface FeasibilityInsightSelection {
  /**
   * The selected thesis ids as sent. An EXPLICIT empty array is a refusal — it
   * means «nothing selected» and is NEVER re-read as «all of them» (AC-W13-21).
   */
  selectedIds: string[];
  /**
   * Categories selected WHOLE. A selection can narrow by category alone (the
   * picker collapses a fully ticked category), which is not «nothing selected».
   */
  selectedCategories?: string[];
  /** Selected ids the pinned analysis does not contain, in the order sent. */
  unknownIds?: string[];
  /** Analysis label for the refusal sentence; falls back to the id. */
  analysisName?: string;
  /** Categories the pinned analysis does not have, in the order sent. */
  unknownCategories?: string[];
  /**
   * Per-scope counts for the zero-thesis blocker and the budget warning —
   * NARROWED scopes only. A scope the selection does not address keeps its
   * default projection and says nothing here (ruling W13-R22).
   */
  scopes?: FeasibilityInsightScope[];
  /** Insight-derived variables whose answer options the selection emptied (I4). */
  variablesWithoutOptions?: Array<{ id: string; name?: string }>;
}

export interface FeasibilityInput {
  pickersLoading?: boolean;
  taskIds: string[];
  analysisId?: string | null;
  /** Selected project variables. */
  projectVariables: Record<string, FeasibilityVariable>;
  /** Selected insight-derived variables. */
  insightVariableIds?: string[];
  /** W13 (IS1): the thesis selection, already resolved against the pinned analysis. */
  insightSelection?: FeasibilityInsightSelection;
}

export interface FeasibilityBlocker {
  code: FeasibilityBlockerCode;
  message: string;
  variableIds?: string[];
  fix?: 'select_routes' | 'pin_analysis';
  /** W13: the task a `task_without_theses` blocker is about. */
  taskId?: string;
  /** W13: the offending thesis id of an `insight_not_in_analysis` blocker. */
  insightId?: string;
  /** W13: the offending category of an `insight_not_in_analysis` blocker. */
  category?: string;
}

export interface FeasibilityResult {
  state: FeasibilityState;
  blockers: FeasibilityBlocker[];
  warnings: FeasibilityWarning[];
  facts: {
    routes: number;
    projectVariables: number;
    globalVariables: number;
    taskScopedVariables: number;
    requiredGlobalIds: string[];
    insightVariables: number;
    /** W13: theses the run is grounded in. Absent = every thesis (no selection). */
    selectedInsights?: number;
  };
}

/** The scope's own budget: the global package carries twice as many as a task's. */
export function insightSelectionBudget(scope: 'global' | 'task'): number {
  return scope === 'global' ? GLOBAL_INSIGHT_SELECTION_BUDGET : TASK_INSIGHT_SELECTION_BUDGET;
}

/** «Global scope» / «Task "Lose weight"» — the label every selection sentence opens with. */
export function insightScopeLabel(scope: FeasibilityInsightScope): string {
  if (scope.scope === 'global') return 'Global scope';
  return `Task "${scope.taskName ?? scope.taskId ?? 'unnamed'}"`;
}

/**
 * W13 (AC-W13-04/20/21): every sentence the thesis selection can produce, in one
 * place, so the wizard row, the server 400 and the CLI print the same words.
 */
export const INSIGHT_SELECTION_EMPTY_MESSAGE =
  'No insight theses are selected. Select at least one, or remove the selection to use all of them.';

export function insightNotInAnalysisMessage(insightId: string, analysisLabel: string): string {
  return `Insight "${insightId}" is not part of the pinned analysis ${analysisLabel}. `
    + 'Re-open the Insights step and pick again.';
}

export function insightCategoryNotInAnalysisMessage(category: string, analysisLabel: string): string {
  return `Insight category "${category}" is not part of the pinned analysis ${analysisLabel}. `
    + 'Re-open the Insights step and pick again.';
}

/** I4: the selection left an insight-derived variable with no answer options. */
export function insightVariableWithoutOptionsMessage(variable: { id: string; name?: string }): string {
  return `Variable "${variable.name ?? variable.id}" loses every answer option to this selection. `
    + 'Select a thesis of its category, or drop the variable from the run.';
}

export function scopeWithoutThesesMessage(scope: FeasibilityInsightScope): string {
  return scope.scope === 'global'
    ? 'The global scope has no selected insight theses. '
      + 'Select at least one thesis for it, or clear the selection to use all of them.'
    : `${insightScopeLabel(scope)} has no selected insight theses. `
      + 'Select at least one thesis for it, or remove the task.';
}

export function insightSelectionBudgetMessage(scope: FeasibilityInsightScope): string {
  return `${insightScopeLabel(scope)}: ${scope.selectedCount} theses selected, `
    + `above the ${insightSelectionBudget(scope.scope)} recommended; the prompt and its cost grow.`;
}

const isRequired = (v: FeasibilityVariable) => v.required === true || v.priority === 'required';
const isGlobal = (v: FeasibilityVariable) => !Array.isArray(v.taskIds) || v.taskIds.length === 0;
const names = (vars: FeasibilityVariable[]) => vars.map(v => v.name ?? v.id);

function joinNames(list: string[]): string {
  return list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

export function assessStrategyGenerationFeasibility(input: FeasibilityInput): FeasibilityResult {
  const vars = Object.values(input.projectVariables ?? {});
  const globals = vars.filter(isGlobal);
  const requiredGlobals = globals.filter(isRequired);
  const selection = input.insightSelection;
  const facts = {
    routes: input.taskIds.length,
    projectVariables: vars.length,
    globalVariables: globals.length,
    taskScopedVariables: vars.length - globals.length,
    requiredGlobalIds: requiredGlobals.map(v => v.id),
    insightVariables: input.insightVariableIds?.length ?? 0,
    ...(selection ? { selectedInsights: selection.selectedIds.length } : {}),
  };
  if (input.pickersLoading) return { state: 'checking', blockers: [], warnings: [], facts };

  const blockers: FeasibilityBlocker[] = [];
  if (!input.analysisId) {
    blockers.push({
      code: 'analysis_not_pinned',
      message: 'No insight analysis pinned. Pick one analysis to ground generation.',
      fix: 'pin_analysis',
    });
  }
  if (input.taskIds.length === 0) {
    const req = names(requiredGlobals);
    blockers.push({
      code: 'no_task_routes',
      fix: 'select_routes',
      variableIds: requiredGlobals.map(v => v.id),
      message: req.length
        ? `No task routes selected. ${joinNames(req)} ${req.length === 1 ? 'is' : 'are'} required and ${req.length === 1 ? 'has' : 'have'} nowhere to be collected.`
        : 'No task routes selected. Generation builds one funnel branch per task route.',
    });
  }
  if (vars.length === 0 && facts.insightVariables === 0) {
    blockers.push({
      code: 'empty_variable_package',
      message: 'No variables selected. Generation needs at least one variable to collect.',
    });
  }

  // W13 (IS1): the thesis selection. An ABSENT selection is today's projection
  // and says nothing here; an explicit one is answered before any task exists.
  if (selection) {
    const nothingSelected = selection.selectedIds.length === 0
      && (selection.selectedCategories?.length ?? 0) === 0;
    // AC-W13-21: «nothing selected» is a refusal, NEVER silently widened to
    // «all» — and it is said ONCE, not repeated per starved scope.
    if (nothingSelected) {
      blockers.push({ code: 'task_without_theses', message: INSIGHT_SELECTION_EMPTY_MESSAGE });
    }
    const analysisLabel = selection.analysisName || (input.analysisId ?? 'this analysis');
    for (const insightId of selection.unknownIds ?? []) {
      blockers.push({
        code: 'insight_not_in_analysis',
        insightId,
        message: insightNotInAnalysisMessage(insightId, analysisLabel),
      });
    }
    for (const category of selection.unknownCategories ?? []) {
      blockers.push({
        code: 'insight_not_in_analysis',
        category,
        message: insightCategoryNotInAnalysisMessage(category, analysisLabel),
      });
    }
    for (const scope of nothingSelected ? [] : selection.scopes ?? []) {
      // A scope that holds nothing at all is the async package validator's
      // business (missing/empty package) — this blocker is about a SELECTION
      // that starved a scope which does have theses to offer.
      if (scope.availableCount > 0 && scope.selectedCount === 0) {
        blockers.push({
          code: 'task_without_theses',
          ...(scope.taskId ? { taskId: scope.taskId } : {}),
          message: scopeWithoutThesesMessage(scope),
        });
      }
    }
  }

  const warnings: FeasibilityResult['warnings'] = [];
  for (const scope of selection?.scopes ?? []) {
    if (scope.selectedCount > insightSelectionBudget(scope.scope)) {
      warnings.push({
        code: 'insight_selection_over_budget',
        scopeId: scope.taskId ?? GLOBAL_INSIGHT_SCOPE,
        message: insightSelectionBudgetMessage(scope),
      });
    }
  }
  // I4: an insight-derived variable the selection left optionless is a WARNING —
  // the run still produces a strategy, but that variable degrades silently
  // unless the owner is told which one and why.
  for (const variable of selection?.variablesWithoutOptions ?? []) {
    warnings.push({
      code: 'insight_variable_without_options',
      scopeId: variable.id,
      message: insightVariableWithoutOptionsMessage(variable),
    });
  }
  if (input.taskIds.length === 1) {
    warnings.push({
      code: 'single_route_no_router',
      message: 'One task route: the goal screen will not branch (router needs two or more routes).',
    });
  }

  return { state: blockers.length ? 'blocked' : 'ready', blockers, warnings, facts };
}
